import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/auth";
import AppShell from "@/components/AppShell";
import SessionTimeoutWarning from "@/components/SessionTimeoutWarning";  // Section 7.1

import Landing from "@/pages/Landing";
import Presentacion from "@/pages/Presentacion";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Suppliers from "@/pages/Suppliers";
import SupplierDetail from "@/pages/SupplierDetail";
import MyEvaluation from "@/pages/MyEvaluation";
import Learning from "@/pages/Learning";
import Configuracion from "@/pages/Configuracion";
import Inbox from "@/pages/Inbox";
import SupplierForm from "@/pages/SupplierForm";
import AcceptInvitation from "@/pages/AcceptInvitation";
import MyDocuments from "@/pages/MyDocuments";
import SupplierBulkUpload from "@/pages/SupplierBulkUpload";
import MyQuestionnaire from "@/pages/MyQuestionnaire";
import QuestionnaireReview from "@/pages/QuestionnaireReview";
import PublicSeal from "@/pages/PublicSeal";
import PublicDirectorio from "@/pages/PublicDirectorio";
import RevenueCalculator from "@/pages/RevenueCalculator";
import OperationalFollowup from "@/pages/OperationalFollowup";
import QuestionsAdmin from "@/pages/QuestionsAdmin";
import UsersAdmin from "@/pages/UsersAdmin";
import PillarView from "@/pages/PillarView";
import LearningAdmin from "@/pages/LearningAdmin";
import ProgramSettings from "@/pages/ProgramSettings";
import Reverse360 from "@/pages/Reverse360";
import Reverse360Admin from "@/pages/Reverse360Admin";
import Notifications from "@/pages/Notifications";
import BuyerDashboard from "@/pages/BuyerDashboard";
import CategoryWeights from "@/pages/CategoryWeights";
import ReadinessTecnico from "@/pages/ReadinessTecnico";
import ArchitectureReview from "@/pages/ArchitectureReview";
import DemoSupplier from "@/pages/DemoSupplier";
import DemoAnalytics from "@/pages/DemoAnalytics";
import KpiCatalog from "@/pages/KpiCatalog";
import KpiMatrix from "@/pages/KpiMatrix";
import ImpactoEconomico from "@/pages/ImpactoEconomico";
import CapturaMensual from "@/pages/CapturaMensual";
import GuiaUso from "@/pages/GuiaUso";
import ProgramManifesto from "@/pages/ProgramManifesto";
import FlujoPrograma from "@/pages/FlujoPrograma";
import DevPanel from "@/pages/DevPanel";
import PortalTI from "@/pages/PortalTI";
import Phase2Gate from "@/components/Phase2Gate";
import { PasswordChangeGate } from "@/components/ForcePasswordChange";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Section 6.4: Loading state
  if (loading) return <div className="px-6 py-20 text-center font-hand text-2xl text-[var(--lala-blue)]">Cargando…</div>;
  
  // Section 6.4: Not authenticated - redirect to login
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  // Section 6.4: RBAC check (UX/navigation only - real auth on backend)
  if (roles && !roles.includes(user.role)) {
    const fallback = user.role === "supplier" ? "/mi-evaluacion"
                   : user.role === "buyer" ? "/comprador"
                   : user.role === "owner" ? "/dev"
                   : user.role === "tech_admin" ? "/portal-ti"
                   : "/dashboard";
    return <Navigate to={fallback} replace />;
  }
  
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PasswordChangeGate>
          <AppShell>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/aprendizaje" element={<Learning />} />
              <Route path="/aceptar-invitacion/:token" element={<AcceptInvitation />} />
              <Route path="/sello/:id" element={<PublicSeal />} />
              <Route path="/directorio" element={<PublicDirectorio />} />
              <Route path="/demo" element={<DemoSupplier />} />

              <Route path="/dashboard" element={<Protected roles={["admin", "evaluator", "stakeholder"]}><Dashboard /></Protected>} />
              <Route path="/dashboard/pilar/:pillarKey" element={<Protected roles={["admin", "evaluator", "stakeholder"]}><PillarView /></Protected>} />
              <Route path="/proveedores" element={<Protected roles={["admin", "evaluator", "stakeholder"]}><Suppliers /></Protected>} />
              <Route path="/admin/proveedores/nuevo" element={<Protected roles={["admin"]}><SupplierForm /></Protected>} />
              <Route path="/admin/proveedores/carga-masiva" element={<Protected roles={["admin"]}><SupplierBulkUpload /></Protected>} />
              <Route path="/admin/proveedores/:id/editar" element={<Protected roles={["admin"]}><SupplierForm /></Protected>} />
              <Route path="/proveedores/:id" element={<Protected><SupplierDetail /></Protected>} />
              <Route path="/evaluar" element={<Protected roles={["admin", "evaluator"]}><Navigate to="/captura-mensual" replace /></Protected>} />
              <Route path="/mi-evaluacion" element={<Protected roles={["supplier"]}><MyEvaluation /></Protected>} />
              <Route path="/mis-documentos" element={<Protected roles={["supplier"]}><Phase2Gate flag="supplier_document_upload_enabled" title="Carga documental — Fase 2"><MyDocuments /></Phase2Gate></Protected>} />
              <Route path="/mi-cuestionario" element={<Protected roles={["supplier"]}><Phase2Gate flag="supplier_self_assessment_enabled" title="Cuestionario propio — Fase 2"><MyQuestionnaire /></Phase2Gate></Protected>} />
              <Route path="/admin/cuestionarios" element={<Protected roles={["admin"]}><QuestionnaireReview /></Protected>} />
              <Route path="/admin/configuracion" element={<Protected roles={["admin"]}><Configuracion /></Protected>} />
              <Route path="/presentacion" element={<Protected roles={["admin", "stakeholder"]}><Presentacion /></Protected>} />
              <Route path="/admin/aprobaciones" element={<Protected roles={["admin"]}><Inbox /></Protected>} />
              <Route path="/admin/calculadora" element={<Protected roles={["admin"]}><RevenueCalculator /></Protected>} />
              <Route path="/admin/seguimiento" element={<Protected roles={["admin"]}><OperationalFollowup /></Protected>} />
              <Route path="/admin/preguntas" element={<Protected roles={["admin"]}><QuestionsAdmin /></Protected>} />
              <Route path="/admin/usuarios" element={<Protected roles={["admin"]}><UsersAdmin /></Protected>} />
              <Route path="/admin/aprendizaje" element={<Protected roles={["admin", "owner"]}><LearningAdmin /></Protected>} />
              <Route path="/admin/programa" element={<Protected roles={["admin"]}><ProgramSettings /></Protected>} />
              <Route path="/admin/catalogo-kpis" element={<Protected roles={["admin", "evaluator", "stakeholder"]}><KpiCatalog /></Protected>} />
              <Route path="/admin/matriz-kpis" element={<Protected roles={["admin"]}><KpiMatrix /></Protected>} />
              <Route path="/impacto-economico" element={<Protected roles={["admin", "owner", "buyer", "evaluator", "stakeholder"]}><ImpactoEconomico /></Protected>} />
              <Route path="/captura-mensual" element={<Protected roles={["admin", "evaluator", "buyer"]}><CapturaMensual /></Protected>} />
              <Route path="/comprador/mis-proveedores" element={<Protected roles={["admin", "owner"]}><SupplierBulkUpload /></Protected>} />
              <Route path="/guia-uso" element={<GuiaUso />} />
              <Route path="/guia" element={<GuiaUso />} />
              <Route path="/programa" element={<ProgramManifesto />} />
              <Route path="/flujo-proceso" element={<FlujoPrograma />} />
              <Route path="/admin/demo-analytics" element={<Protected roles={["owner"]}><DemoAnalytics /></Protected>} />
              <Route path="/voz-proveedor" element={<Protected roles={["supplier"]}><Phase2Gate flag="supplier_feedback_360_enabled" title="Evaluación 360° a Lala — Fase 2"><Reverse360 /></Phase2Gate></Protected>} />
              <Route path="/admin/feedback-lala" element={<Protected roles={["admin"]}><Phase2Gate flag="supplier_feedback_360_enabled" title="Evaluación 360° a Lala — Fase 2"><Reverse360Admin /></Phase2Gate></Protected>} />

              {/* Spec Jun 2026 — new pages */}
              <Route path="/notificaciones" element={<Protected><Notifications /></Protected>} />
              <Route path="/comprador" element={<Protected roles={["buyer", "admin"]}><BuyerDashboard /></Protected>} />
              <Route path="/comprador/pesos" element={<Protected roles={["buyer"]}><CategoryWeights /></Protected>} />
              <Route path="/readiness" element={<Protected roles={["owner", "tech_admin"]}><ReadinessTecnico /></Protected>} />
              <Route path="/architecture-review" element={<Protected roles={["owner", "tech_admin"]}><ArchitectureReview /></Protected>} />
              <Route path="/portal-ti" element={<Protected roles={["owner", "tech_admin", "admin"]}><PortalTI /></Protected>} />
              <Route path="/dev" element={<Protected roles={["owner"]}><DevPanel /></Protected>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {/* Section 7.1: Session timeout warning */}
            <SessionTimeoutWarning />
          </AppShell>
          </PasswordChangeGate>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
